<script lang="ts">
  import type { Snippet } from 'svelte'
  import { useDrag } from '$lib/hooks/useDrag.svelte'
  import { sidebarStore } from '$lib/stores/sidebarStore'
  import { get } from 'svelte/store'
  
  interface Props {
    header: Snippet
    toolbar?: Snippet
    leftContent: Snippet
    rightContent: Snippet
    footer: Snippet
  }
  
  let { header, toolbar, leftContent, rightContent, footer }: Props = $props()
  
  // Sidebar state
  let sidebarState = $state(get(sidebarStore))
  
  // Subscribe to sidebar store changes
  $effect(() => {
    const unsubscribe = sidebarStore.subscribe(state => {
      sidebarState = state
      
      // Add/remove transitioning class to body
      if (state.isTransitioning) {
        document.body.classList.add('sidebar-transitioning')
      } else {
        document.body.classList.remove('sidebar-transitioning')
      }
    })
    
    return () => {
      unsubscribe()
      document.body.classList.remove('sidebar-transitioning')
    }
  })
  
  // Resize functionality
  let leftPanelWidth = $state(65) // percentage
  let isResizing = $state(false)
  let containerRef = $state<HTMLDivElement | null>(null)
  let resizeHandleRef = $state<HTMLDivElement | null>(null)
  
  // Use high-performance drag hook
  useDrag(
    () => resizeHandleRef,
    {
      onDragStart: (e) => {
        e.preventDefault()
        isResizing = true
        // Add resizing class to container for disabling transitions
        containerRef?.classList.add('resizing')
      },
      onDrag: (e) => {
        if (!containerRef) return
        
        const containerRect = containerRef.getBoundingClientRect()
        const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100
        
        // Limit the resize between 30% and 80%
        if (newWidth >= 30 && newWidth <= 80) {
          leftPanelWidth = newWidth
        }
      },
      onDragEnd: () => {
        isResizing = false
        // Remove resizing class after a small delay to ensure smooth transition
        setTimeout(() => {
          containerRef?.classList.remove('resizing')
        }, 0)
      },
      cursor: 'col-resize'
    }
  )
</script>

<!-- 
  EditorContainer: Layout-only component following SRP
  Responsible ONLY for the split-panel layout structure
-->
<div class="editor-container" bind:this={containerRef}>
  {@render header()}
  
  <div class="editor-content">
    <!-- Toolbar -->
    {#if toolbar}
      <aside class="toolbar-panel">
        {@render toolbar()}
      </aside>
    {/if}
    
    <!-- Left Panel -->
    <section 
      class="left-panel" 
      style="{sidebarState.isCollapsed ? 'flex: 1 1 auto' : `flex: 0 0 ${leftPanelWidth}%`}"
    >
      {@render leftContent()}
    </section>
    
    <!-- Resize Handle -->
    <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
    {#if !sidebarState.isCollapsed}
      <div 
        bind:this={resizeHandleRef}
        class="resize-handle"
        class:resizing={isResizing}
        role="separator"
        aria-orientation="vertical"
        aria-label="패널 크기 조절 (더블클릭으로 사이드바 닫기)"
        aria-valuenow={Math.round(leftPanelWidth)}
        aria-valuemin="30"
        aria-valuemax="80"
        aria-valuetext="{Math.round(leftPanelWidth)}%"
        tabindex="0"
        ondblclick={() => {
          // Double-click to collapse sidebar
          sidebarStore.toggle()
        }}
        onkeydown={(e) => {
          if (e.key === 'ArrowLeft') {
            leftPanelWidth = Math.max(30, leftPanelWidth - 2)
            e.preventDefault()
          } else if (e.key === 'ArrowRight') {
            leftPanelWidth = Math.min(80, leftPanelWidth + 2)
            e.preventDefault()
          } else if (e.key === 'Enter' || e.key === ' ') {
            // Enter or Space to toggle sidebar
            sidebarStore.toggle()
            e.preventDefault()
          }
        }}
      >
        <div class="resize-handle-line"></div>
      </div>
    {/if}
    
    <!-- Right Panel -->
    {#if !sidebarState.isCollapsed}
      <section 
        class="right-panel"
        class:transitioning={sidebarState.isTransitioning}
        aria-hidden={sidebarState.isCollapsed}
      >
        {@render rightContent()}
      </section>
    {/if}
  </div>
  
  {@render footer()}
</div>

<style>
  .editor-container {
    display: flex;
    flex-direction: column;
    height: 100%; /* Use 100% instead of 100vh since it's nested */
    overflow: hidden;
    background: var(--bg-primary);
    contain: layout style;
    transform: translateZ(0); /* GPU acceleration */
  }
  
  .editor-content {
    flex: 1 1 auto; /* Allow shrinking */
    display: flex;
    min-height: 0;
    overflow: hidden;
    contain: layout;
    will-change: auto;
  }
  
  .toolbar-panel {
    flex-shrink: 0;
    overflow: hidden;
  }
  
  .left-panel {
    display: flex;
    flex-direction: column;
    background: var(--bg-secondary);
    min-width: 0;
    overflow: hidden; /* Let child components handle scrolling */
    position: relative;
    contain: layout style paint;
    backface-visibility: hidden;
    transform: translateZ(0); /* GPU acceleration */
  }

  /* Only apply transition when sidebar is toggling */
  :global(body.sidebar-transitioning) .left-panel {
    transition: flex 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: flex;
  }
  
  /* Removed unused .resizing .left-panel and .resizing .right-panel selectors */
  
  .right-panel {
    display: flex;
    flex-direction: column;
    background: var(--bg-primary);
    min-width: 0;
    overflow: hidden;
    flex: 1 1 auto;
    contain: layout style paint;
    backface-visibility: hidden;
    transform: translateZ(0); /* GPU acceleration */
  }
  
  .right-panel.transitioning {
    /* No animation - instant display */
    will-change: auto;
  }
  
  /* Resize Handle */
  .resize-handle {
    position: relative;
    width: 5px;
    background: transparent;
    cursor: col-resize;
    transition: background-color 0.15s ease-out;
    flex-shrink: 0;
    border: none;
    padding: 0;
    outline: none;
    will-change: background-color;
    /* Increase hit area for easier grabbing */
    margin: 0 -2px;
    padding: 0 2px;
  }
  
  .resize-handle:focus-visible {
    background: rgba(124, 58, 237, 0.1);
  }
  
  .resize-handle:hover {
    background: rgba(124, 58, 237, 0.1);
  }
  
  .resize-handle.resizing {
    background: rgba(124, 58, 237, 0.15);
  }
  
  .resize-handle-line {
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 1px;
    background: var(--border-primary);
    transform: translateX(-50%);
    transition: all 0.15s ease-out;
    will-change: width, background-color;
  }
  
  .resize-handle:hover .resize-handle-line {
    background: var(--accent-primary);
    width: 2px;
  }
  
  .resize-handle.resizing .resize-handle-line {
    background: var(--accent-primary);
    width: 3px;
    transition: none;
  }
  
  /* Dark mode handled by CSS variables */

  /* Demo mode - fill available height with flex */
  :global(.demo-mode) .editor-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  :global(.demo-mode) .editor-content {
    flex: 1;
    overflow: hidden;
    display: flex;
    min-height: 0;
  }

  :global(.demo-mode) .left-panel,
  :global(.demo-mode) .right-panel {
    overflow-y: auto;
    overflow-x: hidden;
  }

  /* Mobile responsive */
  @media (max-width: 768px) {
    .editor-content {
      flex-direction: column;
    }
    
    .left-panel,
    .right-panel {
      flex: 1 1 50% !important;
      border-right: none;
    }
    
    .left-panel {
      border-bottom: 1px solid #f5f5f5;
    }
    
    .resize-handle {
      display: none;
    }
  }
  
  /* Dark mode mobile */
  @media (prefers-color-scheme: dark) and (max-width: 768px) {
    .left-panel {
      border-bottom: 1px solid #1f2937;
    }
  }
</style>